import { useAppSelector } from '@/hooks/useAppSelector';
import { useFormStyles } from '@/styles/formStyles';
import { Euro, MonetizationOn } from '@material-ui/icons';
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { FieldArray, Form, Formik } from 'formik';
import React, { ChangeEvent } from 'react';
import { validationSchemaRectificativaParcial } from './RectificativaParcialSchema';
import { useDispatch } from 'react-redux';
import { startCreateRectificativaParcial } from '@/redux/actions/facturasService';
import { LoadingButton } from '@mui/lab';
import { useParams } from '@reach/router';
import { calculateFromDollarsToEuro } from '@/utils/utils';
import { formatImporte } from '@/utils/money';

interface LineasRectificativaBase {
  PkLineasVenta: string;
  Unidades: number;
  ReservaEuros: number;
  InSituLineaDolares: number;
  ConceptoLinea: string;
}
interface Rate {
  CambioDolares: number;
  CambioEuros: number;
}
interface LineasRectificativa extends LineasRectificativaBase {
  rate: Rate;
  selected: boolean;
  reventaLineaEuros: number;
}

interface Props {
  toggleDialogClose: () => void;
}

export const RectificativaParcialForm = ({ toggleDialogClose }: Props) => {
  const { activeEdit } = useAppSelector(selector => selector.invoice);
  const { moneda: monedaUsuario } = useAppSelector(state => state.auth);
  const classes = useFormStyles();
  const dispatch = useDispatch();
  const params = useParams();

  const PkVentas = params?.PkVentas;

  interface InitialValuesParcialForm {
    ConceptoRectificativa: string;
    LineasRectificativa: LineasRectificativa[];
  }
  const initialValues: InitialValuesParcialForm = {
    ConceptoRectificativa: '',
    LineasRectificativa: activeEdit.LineasVentaCompuestas.map(linea => ({
      PkLineasVenta: linea.PkLineasVenta,
      Unidades: linea.Unidades ?? 0,
      ReservaEuros: linea.ReservaLineaEuros ?? 0,
      InSituLineaDolares: linea.InSituLineaDolares ?? 0,
      ConceptoLinea: linea.Concepto ?? '',
      selected: false,
      rate: {
        CambioDolares: linea.TipoCambioProveedor.CambioDolares ?? 0,
        CambioEuros: linea.TipoCambioProveedor.CambioEuros ?? 0,
      },
      reventaLineaEuros: linea.ReventaLineaEuros ?? 0,
    })),
  };

  const getImporteVenta = (
    rate: Rate,
    reservaEuros: number,
    insituLineaDolares: number,
    reventaLineaEuros: number,
    unidades: number,
  ): number => {
    return (+reservaEuros + +calculateFromDollarsToEuro(rate, insituLineaDolares) - reventaLineaEuros) * +unidades;
  };

  const getTotalReserva = (lineasRectificativa: LineasRectificativa[]) => {
    return lineasRectificativa
      .map(linea =>
        getImporteVenta(
          linea.rate,
          linea.ReservaEuros,
          linea.InSituLineaDolares,
          linea.reventaLineaEuros,
          linea.Unidades,
        ),
      )
      .reduce((prev, curr) => prev + curr, 0);
  };

  const initialReserva = activeEdit?.ImporteFactura;

  const handleSwtichChange = (
    e: ChangeEvent<HTMLInputElement>,
    index: number,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
  ) => {
    const initialLinea = initialValues.LineasRectificativa[index];

    setFieldValue(`LineasRectificativa.${index}`, {
      ...initialLinea,
      selected: e.target.checked,
    });
  };

  const handleSubmit = (values, { setSubmitting }) => {
    const selectedLines = values.LineasRectificativa.filter(l => l.selected).map(({ selected, rate, ...line }) => ({
      ...line,
      Unidades: Number(line.Unidades),
      ReservaEuros: Number(line.ReservaEuros),
      InSituLineaDolares: Number(line.InSituLineaDolares),
    }));

    if (selectedLines.length === 0) return;

    // Only include selected lines
    const payload = {
      ConceptoRectificativa: values.ConceptoRectificativa,
      LineasRectificativa: selectedLines,
    };

    dispatch(startCreateRectificativaParcial(PkVentas ?? '', payload, setSubmitting, toggleDialogClose));
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchemaRectificativaParcial}
      onSubmit={handleSubmit}>
      {({ values, handleChange, handleBlur, errors, touched, isSubmitting, isValid, setFieldValue }) => (
        <Form placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          {/* CONCEPTO RECTIFICATIVA */}
          <TextField
            fullWidth
            name="ConceptoRectificativa"
            label="Concepto rectificativa"
            value={values.ConceptoRectificativa}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.ConceptoRectificativa && Boolean(errors.ConceptoRectificativa)}
            helperText={touched.ConceptoRectificativa && (errors.ConceptoRectificativa as string)}
            sx={{ mb: 4 }}
            disabled={isSubmitting}
          />

          <FieldArray name="LineasRectificativa">
            {() =>
              values.LineasRectificativa.map((linea, index) => {
                const disabled = isSubmitting || !linea.selected;

                return (
                  <Grid container spacing={2} key={linea.PkLineasVenta} sx={{ mb: 2 }}>
                    <Grid item xs={3}>
                      <Box display={'flex'}>
                        {/* SWITCH AÑADIR LÍNEA */}
                        <Switch
                          checked={linea.selected}
                          onChange={e => handleSwtichChange(e, index, setFieldValue)}
                          disabled={isSubmitting}
                        />
                        {/* CONCEPTO LINEA */}
                        <TextField
                          fullWidth
                          name={`LineasRectificativa.${index}.ConceptoLinea`}
                          label="Concepto línea"
                          value={linea.ConceptoLinea}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          size="small"
                          error={Boolean(
                            touched?.LineasRectificativa?.[index]?.ConceptoLinea &&
                              errors?.LineasRectificativa?.[index]?.ConceptoLinea,
                          )}
                          helperText={
                            touched?.LineasRectificativa?.[index]?.ConceptoLinea &&
                            errors?.LineasRectificativa?.[index]?.[index]?.ConceptoLinea
                          }
                          disabled={disabled}
                        />
                      </Box>
                    </Grid>

                    {/* UNIDADES */}
                    <Grid item xs={1}>
                      <TextField
                        fullWidth
                        name={`LineasRectificativa.${index}.Unidades`}
                        label="Unidades"
                        value={linea.Unidades}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        size="small"
                        error={Boolean(
                          touched?.LineasRectificativa?.[index]?.Unidades &&
                            errors?.LineasRectificativa?.[index]?.Unidades,
                        )}
                        helperText={
                          touched?.LineasRectificativa?.[index]?.Unidades &&
                          errors?.LineasRectificativa?.[index]?.Unidades
                        }
                        disabled={disabled}
                      />
                    </Grid>

                    {/* RESERVA EUROS */}
                    <Grid item xs={2}>
                      <TextField
                        fullWidth
                        name={`LineasRectificativa.${index}.ReservaEuros`}
                        label="Reserva"
                        value={linea.ReservaEuros}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        size="small"
                        error={Boolean(
                          touched?.LineasRectificativa?.[index]?.ReservaEuros &&
                            errors?.LineasRectificativa?.[index]?.ReservaEuros,
                        )}
                        helperText={
                          touched?.LineasRectificativa?.[index]?.ReservaEuros &&
                          errors?.LineasRectificativa?.[index]?.ReservaEuros
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Euro />
                            </InputAdornment>
                          ),
                          classes: { input: classes.resize },
                        }}
                        InputLabelProps={{ style: { fontSize: '0.8em' } }}
                        disabled={disabled}
                      />
                    </Grid>

                    {/* IN SITU DOLARES */}
                    <Grid item xs={2}>
                      <TextField
                        fullWidth
                        name={`LineasRectificativa.${index}.InSituLineaDolares`}
                        label="In situ"
                        value={linea.InSituLineaDolares}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        size="small"
                        error={Boolean(
                          touched?.LineasRectificativa?.[index]?.InSituLineaDolares &&
                            errors?.LineasRectificativa?.[index]?.InSituLineaDolares,
                        )}
                        helperText={
                          touched?.LineasRectificativa?.[index]?.InSituLineaDolares &&
                          errors?.LineasRectificativa?.[index]?.InSituLineaDolares
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <MonetizationOn />
                            </InputAdornment>
                          ),
                          classes: { input: classes.resize },
                        }}
                        InputLabelProps={{ style: { fontSize: '0.8em' } }}
                        disabled={disabled}
                      />
                    </Grid>
                    {/* IN SITU EUROS */}
                    <Grid item xs={2}>
                      <TextField
                        fullWidth
                        label="In situ"
                        value={calculateFromDollarsToEuro(
                          linea.rate,
                          values.LineasRectificativa?.[index]?.InSituLineaDolares,
                        )}
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Euro />
                            </InputAdornment>
                          ),
                          classes: { input: classes.resize },
                        }}
                        InputLabelProps={{ style: { fontSize: '0.8em' } }}
                        disabled
                      />
                    </Grid>
                    {/* IMPORTE VENTA */}
                    <Grid item xs={2}>
                      <TextField
                        fullWidth
                        label="Importe factura"
                        value={getImporteVenta(
                          values.LineasRectificativa?.[index]?.rate,
                          values.LineasRectificativa?.[index]?.ReservaEuros,
                          values.LineasRectificativa?.[index]?.InSituLineaDolares,
                          values.LineasRectificativa?.[index]?.reventaLineaEuros,
                          values.LineasRectificativa?.[index]?.Unidades,
                        )}
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Euro />
                            </InputAdornment>
                          ),
                          classes: { input: classes.resize },
                        }}
                        InputLabelProps={{ style: { fontSize: '0.8em' } }}
                        disabled
                      />
                    </Grid>

                    {index < values.LineasRectificativa.length - 1 && (
                      <Grid item xs={12}>
                        <Divider sx={{ my: 3 }} />
                      </Grid>
                    )}
                  </Grid>
                );
              })
            }
          </FieldArray>

          <Box display={'flex'} flexDirection={'row'} gap={1} alignItems={'center'} marginTop={6}>
            <Typography style={{ fontSize: 24, fontWeight: 'bold' }}>Importe Factura:</Typography>
            <Typography style={{ fontSize: 24 }}>
              {formatImporte(getTotalReserva(values.LineasRectificativa) - initialReserva, monedaUsuario)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
            <Button
              style={{ marginRight: '20px', backgroundColor: '#F39868', color: '#fff', borderRadius: 7 }}
              onClick={() => toggleDialogClose()}>
              <Typography style={{ fontWeight: 'bold' }}>Cancelar</Typography>
            </Button>
            <LoadingButton
              variant="contained"
              type="submit"
              style={{ backgroundColor: '#E5824F', color: 'white', opacity: isValid && !isSubmitting ? '100%' : '80%' }}
              disabled={isSubmitting || !isValid}
              loading={isSubmitting}>
              Generar
            </LoadingButton>
          </Box>
        </Form>
      )}
    </Formik>
  );
};
